/**
 * The EFFECTIVE verdict of one (framework × article) cell, in ONE place.
 *
 * Two callers need this precedence and must not disagree about it:
 *   - `cellState.ts::cellInputs`, which classifies the cell (and used to own
 *     this function outright);
 *   - `flowState.ts::buildStateContext`, whose published-M6-finding loop runs
 *     BEFORE any `StateContext` exists, so it cannot call the `cellState.ts`
 *     wrapper.
 *
 * It therefore takes the two pieces structurally (the M5 map and the statement
 * facts) rather than a `StateContext`: that type lives in `cellState.ts`, which
 * already imports nothing from `flowState.ts` (the arrow goes the other way,
 * `scopeKey`), and a shared type here would recreate that edge in reverse.
 *
 * Twin: `mc_declaration_cell_state.py::_resolve_cell_verdict` on the server.
 * Keep the precedence in step.
 */

import { memberOf } from "./memberSets"
import type { Verdict } from "./studioTypes"

/** The minimal statement shape this resolution reads. `StatementFacts`
 *  (`cellState.ts`) satisfies it structurally. */
export interface CoveringStatement {
  framework: string
  verdict: Verdict | null
  selectedArticles: string[]
  /** The staged row's own review status is `rejected` — the statement states
   *  nothing any more. Only `resolveFindingVerdict` reads it; optional so
   *  `StatementFacts` (which carries no such field) still satisfies the shape
   *  for `resolveCellVerdict`. */
  rejected?: boolean
  /** A REVIEWER DECIDED this statement's verdict: a saved `verdictOverride`
   *  (what the dropdown writes) or a `manuallyAdded` row. NOT a `confirmedValues`
   *  signature — see `resolveFindingVerdict` for the three reasons. Only that
   *  function reads this, and only it may outrank M5. */
  reviewerDecided?: boolean
}

/** The minimal M5 map shape: `fw:articleNumber` to the published per-cell
 *  verdict. `StateContext["cellVerdicts"]` satisfies it structurally. */
export type CellVerdictMap = Record<string, { verdict: Verdict } | undefined>

/** Urgency order: a CWC / non-compliant reading (more scrutiny) outranks a
 *  plain compliant one when several statements cover one cell. */
export function verdictSeverity(v: Verdict): number {
  return v === "compliant"
    ? 0
    : v === "compliant_with_conditions"
      ? 1
      : v === "non_compliant"
        ? 2
        : 0
}

/** M5's per-cell verdict wins; else the most severe covering statement verdict;
 *  else null (no statement touches the cell). */
export function resolveCellVerdict(
  cellVerdicts: CellVerdictMap,
  statements: readonly CoveringStatement[],
  framework: string,
  articleId: string,
): Verdict | null {
  const cv = cellVerdicts[`${framework}:${articleId}`]?.verdict
  if (cv) return cv
  const covering = statements.filter(
    (s) =>
      s.verdict &&
      s.framework === framework &&
      memberOf(s.selectedArticles, articleId),
  )
  if (covering.length === 0) return null
  return covering
    .map((s) => s.verdict!)
    .sort((a, b) => verdictSeverity(b) - verdictSeverity(a))[0]
}

/**
 * The verdict that decides whether a PUBLISHED M6 condition finding still
 * applies. Three tiers, in order:
 *
 *   1. a live statement a REVIEWER DECIDED (saved `verdictOverride`, or a
 *      `manuallyAdded` row) — most severe of those;
 *   2. M5's per-cell entry;
 *   3. the most severe live covering statement verdict (untouched extraction),
 *      for a run that published no cell verdict at all.
 *
 * Tier 1 exists because the reviewer's verdict edit saves without recomputing
 * anything, so `article_verdicts` keeps the run's reading forever, and it
 * carries an entry for EVERY covered cell (M5's own cells plus the capped ones
 * filled in by `article_cap.synthesize_inherited_verdicts`). Without it a stale
 * `compliant_with_conditions` cell verdict would outlive every verdict a human
 * can set and the finding would never clear, which is the live-run
 * symptom this fix exists for. A human decision is newer than the run.
 *
 * A CONFIRMATION IS NOT SUCH A DECISION, though it is a human act on the same
 * statement (TUR-1211, sandbox DEC-2026-0007, and the review of this fix's first
 * draft, which admitted it here and was wrong three ways):
 *   - tier 1 CLOSES the finding, so the cell would land in neither the required
 *     nor the softened set and M8's own tier-1 auto decision would green it with
 *     nobody having answered the finding at all;
 *   - `confirm_statement` FIRES THE CONE, so M5's conditional reading after a
 *     confirm is NEWER than the signature, not stale behind it — the staleness
 *     argument that justifies tier 1 for a save-only verdict edit runs backwards;
 *   - the signature survives `update_statement_scope` untouched, so one made
 *     before an article joined the statement would close a finding on an article
 *     it never spoke about.
 * The signature is about the statement's IDENTITY; the finding is about a CELL.
 * The reviewer's way out of that shape is the SOFTENED state
 * (`flowState.ts::buildStateContext`) plus the cell's own `__verified__` mark.
 *
 * Tier 2 sits ABOVE untouched extraction on purpose (Codex review, finding 1):
 * M5 is not a stale copy of the statement. `determine_verdicts` reconciles
 * article-specific evidence independently, so an extracted compliant blanket
 * statement can legitimately coexist with a valid M5 conditional cell verdict
 * and a real finding — a table row disclosing lead under an exemption while the
 * letter says compliant. Letting the untouched statement drop that finding would
 * close it with no human deciding anything, and the live verdict-basis loop
 * could not restore it (the statement reads compliant).
 *
 * A REJECTED statement is not a cover: the staged row's own review status takes
 * it out of what approve writes to master data, so it states nothing and must
 * not hold a finding alive through the severity max (the tester's second step,
 * where the conditional statement was rejected and a manual compliant one added
 * over the same article). Same status source the edition axis and the override
 * views already read. A cell whose ONLY cover is rejected therefore has no live
 * human verdict at all and falls back to M5, exactly like a cell no statement
 * covers: the run's reading is the only reading left.
 *
 * This is NOT a change to the classifier's precedence: `resolveCellVerdict` (M5
 * first, rejection-blind) stays exactly as it was everywhere else, including the
 * live verdict-basis loop in `flowState.ts` (W5-L3b), which asks a different
 * question about a statement already known to be conditional.
 *
 * Twin: `mc_declaration_cell_state.py::_resolve_finding_verdict`, keep in step.
 */
export function resolveFindingVerdict(
  cellVerdicts: CellVerdictMap,
  statements: readonly CoveringStatement[],
  framework: string,
  articleId: string,
): Verdict | null {
  const covering = statements.filter(
    (s) =>
      s.verdict &&
      !s.rejected &&
      s.framework === framework &&
      memberOf(s.selectedArticles, articleId),
  )
  const mostSevere = (pool: readonly CoveringStatement[]): Verdict =>
    pool
      .map((s) => s.verdict!)
      .sort((a, b) => verdictSeverity(b) - verdictSeverity(a))[0]

  const decided = covering.filter((s) => s.reviewerDecided)
  if (decided.length > 0) return mostSevere(decided)
  const published = cellVerdicts[`${framework}:${articleId}`]?.verdict
  if (published) return published
  return covering.length > 0 ? mostSevere(covering) : null
}

/**
 * Whether a cell with this effective verdict still demands condition data at
 * all. ONLY `compliant_with_conditions` does, the same single rule
 * `rowComplete` (`conditions.ts`) applies: a `non_compliant` cell cannot carry
 * complete required conditional data by definition (B6), and a plain
 * `compliant` one has nothing to substantiate.
 *
 * A null verdict (no statement covers the cell, no M5 entry) is NOT an answer,
 * so it keeps whatever the pipeline published: the caller decides.
 */
export function verdictNeedsConditionData(verdict: Verdict | null): boolean {
  return verdict === null || verdict === "compliant_with_conditions"
}
